<template>
  <div class="sign-module">
    <PixelCatRunner :speed="0.8" :cat-type="2" />
    <div class="sign-container">
      <!-- 左侧：签到区 -->
      <div class="sign-left">
        <div class="sign-input-row">
          <input v-model="signNickname" placeholder="昵称" class="sign-input" maxlength="20" />
          <button class="sign-btn" @click="doSign" :disabled="signing || signCooldown">
            {{ signing ? '签到中...' : (signCooldown ? '冷却中...' : (signNickname.trim() ? `${signNickname.trim()} 签到` : '签到')) }}
          </button>
        </div>
        <div class="sign-calendar">
          <div class="calendar-header">
            <button class="calendar-nav" @click="prevMonth">‹</button>
            <span class="calendar-title">{{ currentYear }}年{{ currentMonth + 1 }}月</span>
            <button class="calendar-nav" @click="nextMonth">›</button>
          </div>
          <div class="calendar-weekdays">
            <span v-for="w in weekDays" :key="w">{{ w }}</span>
          </div>
          <div class="calendar-days">
            <div v-for="(day, idx) in calendarDays" :key="idx"
              class="calendar-day"
              :class="{ signed: day.signed, empty: !day.value, today: day.isToday }">
              {{ day.value || '' }}
            </div>
          </div>
        </div>
      </div>
      <!-- 右侧：Tab 切换 -->
      <div class="sign-right">
        <!-- Tab 导航 -->
        <div class="tab-bar">
          <button class="tab-btn" :class="{ active: activeTab === 'leaderboard' }" @click="activeTab = 'leaderboard'">排行榜</button>
          <button class="tab-btn" :class="{ active: activeTab === 'rules' }" @click="switchToRules">兑礼规则</button>
          <button class="tab-btn" :class="{ active: activeTab === 'records' }" @click="switchToRecords">兑礼记录</button>
        </div>

        <!-- 排行榜 -->
        <div class="tab-content" v-show="activeTab === 'leaderboard'">
          <div class="leaderboard-list">
            <div v-for="(item, idx) in leaderboard" :key="idx" class="leaderboard-item">
              <span class="rank-num" :class="'rank-' + (idx + 1)">{{ idx + 1 }}</span>
              <span class="rank-name">{{ item.nickname }}</span>
              <span class="rank-count">{{ item.count }} 天</span>
            </div>
            <div v-if="leaderboard.length === 0" class="tab-empty">暂无签到记录</div>
          </div>
        </div>

        <!-- 兑礼规则 -->
        <div class="tab-content" v-show="activeTab === 'rules'">
          <div class="sign-point-info">当前签到天数：<strong>{{ signPointNumber }}</strong> 天</div>
          <div class="reward-list">
            <div v-for="item in targetList" :key="item.id" class="reward-item">
              <div class="reward-info">
                <span class="reward-target">达成 {{ item.targetNum }} 天</span>
                <span class="reward-desc">{{ item.targetDescription }}</span>
              </div>
              <button
                class="exchange-btn"
                :disabled="exchanging || exchangeCooldown || signPointNumber < item.targetNum"
                @click="doExchange(item)"
              >
                {{ exchanging ? '兑换中...' : (exchangeCooldown ? '冷却中...' : '兑换') }}
              </button>
            </div>
          </div>
          <div v-if="targetList.length === 0" class="tab-empty">暂无奖励配置</div>
        </div>

        <!-- 兑礼记录 -->
        <div class="tab-content" v-show="activeTab === 'records'">
          <div v-if="exchangeRecords.length === 0" class="tab-empty">暂无兑换记录</div>
          <div v-for="record in exchangeRecords" :key="record.id" class="record-item">
            <div class="record-info">
              <span class="record-desc">{{ record.targetDescription }}</span>
              <span class="record-code">兑奖码：<strong>{{ record.exchangeCode }}</strong></span>
              <span class="record-points">消耗 {{ record.consumedPoints }} 天 · 剩余 {{ record.remainingPoints }} 天</span>
              <span class="record-time">{{ formatTime(record.exchangeTime) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '../api/api';

import PixelCatRunner from './PixelCatRunner.vue';

export default {
  name: 'SignModule',
  components: { PixelCatRunner },
  props: {
    signRankName: {
      type: String,
      default: '周榜',
    },
    signInfo: {
      type: Object,
      default: null,
    },
    userNickname: {
      type: String,
      default: '',
    },
    targetList: {
      type: Array,
      default: function () { return []; },
    },
    signPointNumber: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      signNickname: localStorage.getItem('sign_nickname') || '',
      weekDays: ['日', '一', '二', '三', '四', '五', '六'],
      signing: false,
      signCooldown: false,
      signDates: [],
      leaderboard: [],
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth(),
      activeTab: 'leaderboard',
      exchangeRecords: [],
      exchanging: false,
      exchangeCooldown: false,
    };
  },
  computed: {
    calendarDays() {
      const year = this.currentYear;
      const month = this.currentMonth;
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const signTimestamps = this.signDates.map(function (ts) {
        const d = new Date(ts);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      });
      const signSet = new Set(signTimestamps);
      const days = [];
      for (let i = 0; i < firstDay; i++) {
        days.push({ value: 0, signed: false, isToday: false, empty: true });
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        date.setHours(0, 0, 0, 0);
        const isToday = date.getTime() === today.getTime();
        const signed = signSet.has(date.getTime());
        days.push({ value: d, signed: signed, isToday: isToday, empty: false });
      }
      return days;
    },
  },
  watch: {
    signInfo: {
      immediate: true,
      handler(val) {
        if (val) {
          this.signDates = val.signDates || [];
          this.leaderboard = val.leaderboard || [];
        }
      },
    },
    userNickname: {
      immediate: true,
      handler(val) {
        if (val) {
          this.signNickname = val;
          localStorage.setItem('sign_nickname', val);
        }
      },
    },
  },
  methods: {
    async loadSignData() {
      // 优先使用 prop 传入的 signInfo
      if (this.signInfo) {
        this.signDates = this.signInfo.signDates || [];
        this.leaderboard = this.signInfo.leaderboard || [];
        return;
      }
      // 回退到独立请求
      try {
        const nickname = this.signNickname || '';
        const data = await API.getSignInfo();
        if (data) {
          this.signDates = data.signDates || [];
          this.leaderboard = data.leaderboard || [];
        }
      } catch (e) {
        console.error('加载签到数据失败:', e);
      }
    },
    async doSign() {
      const nickname = this.signNickname.trim();
      if (!nickname) {
        this.$emit('show-toast', '请先输入昵称');
        return;
      }
      localStorage.setItem('sign_nickname', nickname);
      this.signing = true;
      try {
        const result = await API.signCheckin(nickname);
        if (result && result.signed) {
          this.$emit('show-toast', '签到成功 ✨');
          this.$emit('sign-success');
          await this.loadSignData();
        } else {
          this.$emit('show-toast', result && result.message ? result.message : '今日已签到');
        }
      } catch (e) {
        console.error('签到失败:', e);
        this.$emit('show-toast', '签到失败，请稍后重试');
      } finally {
        this.signing = false;
        this.signCooldown = true;
        setTimeout(() => { this.signCooldown = false; }, 2000);
      }
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear -= 1;
      } else {
        this.currentMonth -= 1;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear += 1;
      } else {
        this.currentMonth += 1;
      }
    },
    switchToRules() {
      this.activeTab = 'rules';
    },
    switchToRecords() {
      this.activeTab = 'records';
      this.loadExchangeRecords();
    },
    async doExchange(item) {
      if (this.exchanging) return;
      this.exchanging = true;
      try {
        const result = await API.exchangeReward(item.id);
        if (result && result.success) {
          const msg = result.exchangeCode
            ? result.message + '（兑奖码：' + result.exchangeCode + '）'
            : result.message;
          this.$emit('show-toast', msg);
          // 通知父组件更新签到天数
          this.$emit('exchange-success', item.targetNum);
        } else {
          this.$emit('show-toast', result && result.message ? result.message : '兑换失败');
        }
      } catch (e) {
        console.error('兑换失败:', e);
        this.$emit('show-toast', '兑换失败，请稍后重试');
      } finally {
        this.exchanging = false;
        this.exchangeCooldown = true;
        setTimeout(() => { this.exchangeCooldown = false; }, 2000);
      }
    },
    async loadExchangeRecords() {
      try {
        const records = await API.getExchangeRecords();
        this.exchangeRecords = records || [];
      } catch (e) {
        console.error('加载兑换记录失败:', e);
        this.$emit('show-toast', '加载兑换记录失败');
      }
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const y = date.getFullYear();
      const m = ('0' + (date.getMonth() + 1)).slice(-2);
      const d = ('0' + date.getDate()).slice(-2);
      const h = ('0' + date.getHours()).slice(-2);
      const min = ('0' + date.getMinutes()).slice(-2);
      return y + '-' + m + '-' + d + ' ' + h + ':' + min;
    },
  },
  mounted() {
    this.loadSignData();
  },
};
</script>

<style scoped>
.sign-module {
  margin: 16px 0 20px;
}

.sign-container {
  display: flex;
  gap: 16px;
  background: linear-gradient(135deg, var(--glass-strong), var(--glass-bg));
  border: 3px solid var(--pixel-dark);
  border-radius: 8px;
  padding: 16px;
  box-shadow:
    0 0 0 2px var(--pixel-ice),
    0 0 0 4px var(--pixel-blue),
    0 5px 20px var(--shadow-soft),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.sign-left {
  flex: 1;
  min-width: 0;
}

.sign-input-row {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.sign-input {
  flex: 1;
  border: 3px solid var(--pixel-dark);
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--glass-strong);
  outline: none;
  transition: var(--transition-smooth);
}

.sign-input:focus {
  border-color: var(--pixel-lavender);
  box-shadow: 0 0 16px var(--glow-blue);
}

.sign-btn {
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  color: var(--pixel-dark);
  border: 3px solid var(--pixel-dark);
  border-radius: 4px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: var(--transition-bounce);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 4px 0 var(--pixel-shadow-dark),
    0 4px 14px var(--shadow-glow);
  white-space: nowrap;
}

.sign-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  filter: brightness(1.05);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 6px 0 var(--pixel-shadow-dark),
    0 6px 20px var(--shadow-glow);
}

.sign-btn:active:not(:disabled) {
  transform: translateY(3px);
  box-shadow:
    inset 0 3px 0 var(--pixel-shadow-web),
    0 1px 0 var(--pixel-shadow-dark);
}

.sign-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 日历 */
.sign-calendar {
  background: var(--glass-strong);
  border-radius: 6px;
  padding: 10px;
  border: 3px solid var(--pixel-dark);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 3px 0 var(--pixel-shadow-dark),
    0 4px 12px var(--shadow-soft);
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.calendar-nav {
  width: 28px;
  height: 28px;
  border: 2px solid var(--pixel-dark);
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  border-radius: 3px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  color: var(--pixel-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-smooth);
  font-family: inherit;
  line-height: 1;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.calendar-nav:active {
  transform: translateY(2px);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 0 0 var(--pixel-shadow-dark);
}

.calendar-nav:hover {
  filter: brightness(1.08);
}

.calendar-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--pixel-dark);
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 11px;
  color: var(--text-light);
  margin-bottom: 4px;
  font-weight: 600;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.calendar-day {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: var(--text-primary);
  transition: var(--transition-smooth);
  position: relative;
}

.calendar-day.empty {
  visibility: hidden;
}

.calendar-day.signed {
  color: white;
  font-weight: 700;
  z-index: 1;
}

.calendar-day.signed::before {
  content: '';
  position: absolute;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  z-index: -1;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark),
    0 2px 6px var(--shadow-glow);
}

.calendar-day.signed.today {
  box-shadow: none;
}

.calendar-day.signed.today::before {
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark),
    0 0 0 3px var(--pixel-pink),
    0 2px 6px var(--shadow-glow);
}

.calendar-day.today {
  font-weight: 700;
  color: var(--dream-accent);
  background: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px var(--pixel-blue);
}

/* 右侧面板 */
.sign-right {
  width: 260px;
  flex-shrink: 0;
  background: var(--glass-strong);
  border-radius: 6px;
  padding: 0;
  border: 3px solid var(--pixel-dark);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 3px 0 var(--pixel-shadow-dark),
    0 4px 12px var(--shadow-soft);
}

/* Tab 导航 */
.tab-bar {
  display: flex;
  border-bottom: 3px solid var(--pixel-dark);
  background: var(--glass-bg);
}

.tab-btn {
  flex: 1;
  padding: 8px 4px;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;
  transition: var(--transition-smooth);
  position: relative;
}

.tab-btn::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--pixel-blue), var(--pixel-lavender));
  transition: var(--transition-smooth);
}

.tab-btn.active {
  color: var(--pixel-dark);
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
}

.tab-btn.active::after {
  width: 100%;
}

.tab-btn:hover:not(.active) {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.3);
}

.tab-content {
  padding: 12px;
  overflow-y: auto;
  max-height: 320px;
}

/* 排行榜列表 */
.leaderboard-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.leaderboard-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  border: 2px solid var(--pixel-dark);
  background: var(--glass-bg);
  transition: var(--transition-smooth);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.leaderboard-item:hover {
  background: var(--glass-strong);
  transform: translateX(4px);
}

.rank-num {
  width: 24px;
  height: 24px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  border: 2px solid var(--pixel-dark);
  background: var(--pixel-pale);
  color: var(--pixel-dark);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.rank-num.rank-1 {
  background: linear-gradient(180deg, #FFD700, #FFA500);
  border-color: #B8860B;
  color: white;
  box-shadow:
    inset 0 2px 0 rgba(255,255,255,0.4),
    0 2px 0 #B8860B;
}

.rank-num.rank-2 {
  background: linear-gradient(180deg, #C0C0C0, #A0A0A0);
  border-color: #888;
  color: white;
  box-shadow:
    inset 0 2px 0 rgba(255,255,255,0.4),
    0 2px 0 #888;
}

.rank-num.rank-3 {
  background: linear-gradient(180deg, #CD7F32, #B8722A);
  border-color: #8B5E3C;
  color: white;
  box-shadow:
    inset 0 2px 0 rgba(255,255,255,0.3),
    0 2px 0 #8B5E3C;
}

.rank-name {
  flex: 1;
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-count {
  font-size: 12px;
  color: var(--dream-accent);
  font-weight: 600;
  white-space: nowrap;
}

.tab-empty {
  text-align: center;
  color: var(--text-light);
  font-size: 13px;
  padding: 24px 0;
}

/* 签到天数提示（兑礼规则内） */
.sign-point-info {
  text-align: center;
  font-size: 13px;
  color: var(--text-primary);
  margin-bottom: 12px;
  padding: 8px;
  background: var(--glass-bg);
  border-radius: 4px;
  border: 2px solid var(--pixel-dark);
}

.sign-point-info strong {
  color: var(--dream-accent);
  font-size: 16px;
}

/* 兑礼规则列表 */
.reward-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reward-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--glass-bg);
  border-radius: 4px;
  border: 2px solid var(--pixel-dark);
  transition: var(--transition-smooth);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.reward-item:hover {
  border-color: var(--pixel-blue);
}

.reward-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.reward-target {
  font-size: 11px;
  color: var(--dream-accent);
  font-weight: 600;
}

.reward-desc {
  font-size: 12px;
  color: var(--text-primary);
  line-height: 1.4;
  overflow-wrap: break-word;
  word-break: break-word;
}

.exchange-btn {
  padding: 5px 14px;
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  color: var(--pixel-dark);
  border: 2px solid var(--pixel-dark);
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: var(--transition-smooth);
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.exchange-btn:hover:not(:disabled) {
  transform: scale(1.05);
  filter: brightness(1.06);
}

.exchange-btn:active:not(:disabled) {
  transform: translateY(2px);
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 0 0 var(--pixel-shadow-dark);
}

.exchange-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 兑礼记录 */
.record-item {
  padding: 10px 12px;
  background: var(--glass-bg);
  border-radius: 4px;
  border: 2px solid var(--pixel-dark);
  margin-bottom: 8px;
  box-shadow:
    inset 0 2px 0 var(--pixel-shadow-web),
    0 2px 0 var(--pixel-shadow-dark);
}

.record-item:last-child {
  margin-bottom: 0;
}

.record-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.record-desc {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: 600;
}

.record-code {
  font-size: 11px;
  color: var(--dream-accent);
  font-weight: 500;
}

.record-code strong {
  font-family: monospace;
  letter-spacing: 1px;
}

.record-points {
  font-size: 11px;
  color: var(--text-secondary);
}

.record-time {
  font-size: 10px;
  color: var(--text-light);
}

@media (max-width: 768px) {
  .sign-container {
    flex-direction: column;
  }

  .sign-right {
    width: 100%;
  }

  .sign-input-row {
    flex-direction: column;
  }

  .sign-btn {
    width: 100%;
  }

  .tab-content {
    max-height: 220px;
  }
}

@media (max-width: 480px) {
  .sign-container {
    padding: 10px;
    gap: 10px;
  }

  /* 日历在窄屏压缩 */
  .sign-calendar {
    padding: 8px;
  }

  .calendar-day {
    height: 30px;
    font-size: 13px;
  }

  .calendar-day.signed::before {
    width: 24px;
    height: 24px;
  }

  .calendar-title {
    font-size: 12px;
  }

  .calendar-nav {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }

  .sign-input {
    font-size: 12px;
    padding: 7px 12px;
  }

  .sign-btn {
    font-size: 12px;
    padding: 7px 14px;
  }
}
</style>